// ─────────────────────────────────────────────────────────────────────────────
// GuideContent.js — вміст "Guide to Completing Your Personal Data", у форматі,
// що переюзується у двох місцях:
//   1) GuidePage.js — обов'язковий повноекранний гейт з кнопкою "Got it"
//   2) OnboardingPage.js — контекстна підказка по кожному кроку візарда
// Текст навмисно дублює PDF (frontend/public/docs/Freelancer_Data_Entry_Guide.pdf) —
// синхронізація вручну. PDF перегенеровано скриптом на reportlab (не в репо,
// одноразовий білд 31.07.2026) — при зміні контенту нижче не забути оновити
// і PDF, інакше вони розійдуться знову.
// Індекси масиву відповідають STEPS у OnboardingPage.js (0..5).
// ─────────────────────────────────────────────────────────────────────────────

const GUIDE_STEPS = [
  {
    title: 'Personal',
    intro: "Enter your details exactly as they appear on your passport or national ID — this avoids mismatches when booking travel or applying for visas/work permits on your behalf.",
    fields: [
      { label: 'Surname / First name', required: true, note: "Your legal surname and first name, as in your passport." },
      { label: 'Middle name', required: false, note: "Leave blank if you don't have one." },
      { label: 'Date of birth', required: true, note: 'Use the date picker.' },
      { label: 'Nationality', required: true, note: 'The nationality shown on your passport.' },
      { label: 'Gender', required: true, note: 'Select from the list.' },
      { label: 'Marital status', required: true, note: 'Single / Married / Divorced / Widowed.' },
      { label: 'Country / City of birth', required: true, note: 'As per your actual information of Birth.' },
    ],
  },
  {
    title: 'Contact & address',
    fields: [
      { label: 'Phone', required: true, note: 'Include the country code, e.g. +380...' },
      { label: 'WhatsApp', required: true, note: 'The number the production team should use to reach you on WhatsApp.' },
      { label: 'Additional phone', required: false, note: 'A second number, if you have one.' },
      { label: 'Email', required: false, note: 'Locked — this is the email linked to your login and cannot be changed here.' },
      { label: 'Address (country, city, full address, ZIP)', required: true, note: 'Your current residential address.' },
      { label: "Emergency contact (phone & email)", required: true, note: "Contact details of someone other than yourself — family member or close contact — who can be reached in case of an emergency while you're on a project." },
    ],
  },
  {
    title: 'Passport & ID',
    fields: [
      { label: 'Passport number & expiry', required: true, note: 'Make sure the expiry date is correct — an expired passport can block travel bookings and visa applications.' },
      { label: 'Additional passport(s)', required: false, note: 'Add this only if you hold more than one passport (e.g. dual citizenship). You can add, edit or remove these at any time.' },
      { label: 'Passport copy (scan or photo)', required: true, note: "A clear, readable scan or photo of your passport's main information page (the spread with your photo and details)." },
      { label: 'Personal photo', required: true, note: 'ID-style photo on a plain white background — used for your crew badge/profile, not a casual photo.' },
    ],
    // Показує візуальні приклади "добре/погано" для фото та паспорта — саме
    // тут найбільше плутанини: фрілансери часто присилають "casual photo"
    // замість ID-style, або обрізаний/розмитий скан паспорта. Компонент
    // рендериться і в GuidePage.js (повноекранний гейт), і в OnboardingPage.js
    // (контекстна підказка кроку) — обидва просто перевіряють цей прапорець.
    visualExamples: true,
  },
  {
    title: 'Financial',
    intro: 'You can add more than one bank account (for example, if you get paid in different currencies for different clients) using "+ Add another bank".',
    fields: [
      { label: 'Bank name', required: true, note: 'Full name of your bank.' },
      { label: 'IBAN / Account number', required: true, note: 'Double-check every digit — this is where your payments will be sent.' },
      { label: 'SWIFT / BIC', required: true, note: 'Required for international transfers.' },
      { label: 'Account holder full name', required: true, note: 'Must match the legal name on the account — payments can be rejected otherwise.' },
      { label: 'Currency', required: true, note: 'Select from the list, or choose "Other…" to type a currency code.' },
    ],
    warning: '⚠️ Keeping your bank details accurate and up to date is your responsibility. Scull Crew is not liable for payments sent to incorrect details you provided.',
  },
  {
    title: 'Travel & languages',
    fields: [
      { label: 'Port of travel (country, city, airport code)', required: true, note: 'Your nearest/preferred departure airport, used for booking flights — e.g. Ukraine, Kyiv, KBP.' },
      { label: 'Languages', required: true, note: 'Add every language you speak with its level (Native / Fluent / Basic / Enough for work). Add at least one.' },
    ],
  },
  {
    title: 'Professional',
    fields: [
      { label: 'Departments', required: true, note: 'Select every department you work in: LX (Lighting), AX (Audio), VX (Video), RX (Rigging), PX (Power).' },
      { label: 'Level per department', required: true, note: 'For each department selected above, choose every level that applies: Crew chief, Technician, Engineer, Operator.' },
      { label: 'Additional skills', required: false, note: 'Control desks, software, equipment or licenses relevant to your role, e.g. GrandMA3, Avolites, Vectorworks.' },
      { label: 'Supporting documents', required: false, note: 'Certificates, permits or licenses — upload with a short title for each.' },
      { label: 'CV', required: true, note: 'PDF or Word document.' },
      { label: 'Comment', required: false, note: "Anything else you'd like the team to know." },
    ],
  },
];

const GOOD_TO_KNOW = [
  'You can save your progress and come back — nothing is lost between steps.',
  'Once submitted, you can still edit everything later from your Profile page.',
  'Keep your passport/visa expiry dates and bank details current — outdated information can delay project assignments, travel bookings, or payments.',
  "If you have trouble uploading a document or a field won't accept your information, contact your Scull Crew administrator for help.",
];

// ─────────────────────────────────────────────────────────────────────────────
// PhotoAndPassportExamples — візуальні "✓ добре / ✗ погано" приклади для
// Personal photo та Passport copy. Це найчастіша причина відхилених
// документів (casual-фото замість ID-style, обрізаний/розмитий скан
// паспорта) — тому просто текстової вимоги в fields[] виявилось недостатньо.
// Малюнки — CSS-мокапи (не реальні фото/скани, щоб не використовувати чиїсь
// персональні дані як приклад), інтерактивності не має, тому чистий JSX без
// стану.
// ─────────────────────────────────────────────────────────────────────────────

function ExampleCard({ good, caption, children }) {
  return (
    <div style={{
      flex: '1 1 160px', minWidth: '150px', background: 'var(--bg-card)',
      border: `1px solid ${good ? 'var(--success-text)' : 'var(--accent)'}`,
      borderRadius: '10px', padding: '10px', textAlign: 'center',
    }}>
      {children}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px',
        marginTop: '8px', fontSize: '11px', fontWeight: 700,
        color: good ? 'var(--success-text)' : 'var(--accent)',
      }}>
        <span>{good ? '✓' : '✗'}</span>
        <span>{caption}</span>
      </div>
    </div>
  );
}

// ID-фото — силует голови й плечей на чисто білому тлі (SVG, не emoji —
// так значно ближче до реального вигляду фото на документи).
function PhotoGoodSVG() {
  return (
    <svg viewBox="0 0 120 150" style={{ width: '100%', maxWidth: '110px', display: 'block', margin: '0 auto' }}>
      <rect x="0" y="0" width="120" height="150" fill="#ffffff" stroke="#ddd" />
      <circle cx="60" cy="52" r="24" fill="#6b6b6b" />
      <path d="M20,150 L20,124 Q20,96 60,90 Q100,96 100,124 L100,150 Z" fill="#6b6b6b" />
    </svg>
  );
}
function PhotoBadSVG() {
  return (
    <svg viewBox="0 0 120 150" style={{ width: '100%', maxWidth: '110px', display: 'block', margin: '0 auto' }}>
      <defs>
        <linearGradient id="guideBadPhotoBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#5b9bd5" />
          <stop offset="100%" stopColor="#4a7c59" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="120" height="150" fill="url(#guideBadPhotoBg)" />
      {/* Друга людина в кадрі + сонцезахисні окуляри на першій — типові
          причини відхилення "casual photo" замість ID-style. */}
      <circle cx="88" cy="66" r="15" fill="#666" opacity="0.85" />
      <path d="M66,150 L66,130 Q66,114 88,110 Q110,114 110,130 L110,150 Z" fill="#666" opacity="0.85" />
      <circle cx="46" cy="58" r="20" fill="#555" />
      <path d="M16,150 L16,128 Q16,104 46,99 Q76,104 76,128 L76,150 Z" fill="#555" />
      <ellipse cx="39" cy="55" rx="7" ry="4" fill="#1a1a1a" />
      <ellipse cx="53" cy="55" rx="7" ry="4" fill="#1a1a1a" />
    </svg>
  );
}

// Паспорт — повний розворот (ліва порожня сторінка + права сторінка з
// фото/даними/MRZ), у стилі "як реально виглядає скан для акредитації".
// Дані — вигадані, "UTO" — стандартний ICAO-код для зразкових/фіктивних
// паспортів (той самий, що використовують в офіційних specimen-документах).
const MRZ_LINE_1 = 'P<UTOMULLER<<JEAN<ALAIN<<<<<<<<<<<<<<<<<<<<<';
const MRZ_LINE_2 = '1234567890UTO9005142M3005147<<<<<<<<<<<<<06';
const PASSPORT_FIELDS = [
  { label: 'Surname', value: 'MULLER' },
  { label: 'Given names', value: 'JEAN ALAIN' },
  { label: 'Nationality', value: 'FRENCH' },
  { label: 'Date of birth', value: '14 MAY 1990' },
  { label: 'Passport no.', value: '12AB34567' },
];

function PassportPageMarkup() {
  return (
    <>
      <rect x="8" y="8" width="152" height="184" rx="2" fill="#f7f5ee" stroke="#ccc" />
      <rect x="180" y="8" width="152" height="184" rx="2" fill="#f7f5ee" stroke="#ccc" />
      <text x="190" y="26" fontSize="10" fontWeight="700" letterSpacing="1" fill="#333">PASSPORT</text>
      <rect x="272" y="34" width="50" height="64" fill="#ffffff" stroke="#999" />
      <circle cx="297" cy="55" r="10" fill="#888" />
      <path d="M282,98 L282,86 Q282,75 297,72 Q312,75 312,86 L312,98 Z" fill="#888" />
      {PASSPORT_FIELDS.map((f, i) => (
        <g key={f.label} transform={`translate(190, ${40 + i * 24})`}>
          <text x="0" y="0" fontSize="6" fill="#999">{f.label}</text>
          <text x="0" y="10" fontSize="9" fontWeight="700" fill="#222">{f.value}</text>
        </g>
      ))}
      <text x="190" y="176" fontFamily="'Courier New', monospace" fontSize="8.5" letterSpacing="0.5" fill="#222">{MRZ_LINE_1}</text>
      <text x="190" y="188" fontFamily="'Courier New', monospace" fontSize="8.5" letterSpacing="0.5" fill="#222">{MRZ_LINE_2}</text>
    </>
  );
}
function PassportGoodSVG() {
  return (
    <svg viewBox="0 0 340 200" style={{ width: '100%', maxWidth: '260px', display: 'block', margin: '0 auto' }}>
      <rect x="0" y="0" width="340" height="200" fill="#e5e3db" />
      <PassportPageMarkup />
    </svg>
  );
}
function PassportBadSVG() {
  return (
    <svg viewBox="0 0 340 200" style={{ width: '100%', maxWidth: '260px', display: 'block', margin: '0 auto' }}>
      <defs>
        <filter id="guidePassportBlur" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.6" />
        </filter>
        <linearGradient id="guidePassportGlare" x1="0" y1="0" x2="1" y2="1">
          <stop offset="35%" stopColor="#ffffff" stopOpacity="0" />
          <stop offset="55%" stopColor="#ffffff" stopOpacity="0.65" />
          <stop offset="75%" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="340" height="200" fill="#e5e3db" />
      {/* Зсунуто вбік (SVG обрізає за viewBox — імітує обрізаний кадр, де не
          влізла частина сторінки з MRZ) + розмито + відблиск зверху. */}
      <g transform="translate(55, 0)" filter="url(#guidePassportBlur)">
        <PassportPageMarkup />
      </g>
      <rect x="0" y="0" width="340" height="200" fill="url(#guidePassportGlare)" />
    </svg>
  );
}

export function PhotoAndPassportExamples() {
  const rowStyle = { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' };
  const headingStyle = { color: 'var(--text-secondary)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 8px' };
  return (
    <div style={{ marginTop: '14px', paddingTop: '14px', borderTop: '1px dashed var(--border)' }}>
      <p style={headingStyle}>Personal photo — what it should look like</p>
      <div style={rowStyle}>
        <ExampleCard good caption="Plain white background, alone, face clearly visible">
          <PhotoGoodSVG />
        </ExampleCard>
        <ExampleCard caption="Casual photo, sunglasses, colored background, more than one person">
          <PhotoBadSVG />
        </ExampleCard>
      </div>

      <p style={headingStyle}>Passport copy — what it should look like</p>
      <div style={rowStyle}>
        <ExampleCard good caption="Full page, sharp, every field and the MRZ line readable">
          <PassportGoodSVG />
        </ExampleCard>
        <ExampleCard caption="Cropped, blurry, or with glare — the number/MRZ is unreadable">
          <PassportBadSVG />
        </ExampleCard>
      </div>
    </div>
  );
}

export { GUIDE_STEPS, GOOD_TO_KNOW };
